import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useSnackbar} from "../../../contexts/SnackbarContext";
import {
    CheckboxText,
    CheckboxWrapper,
    Header,
    RequiredFieldsNote,
    Row,
    StyledCard,
    StyledForm,
    StyledLink,
    StyledLinkWrapper,
    StyledTextField,
    Subtext
} from "./Register.styled";
import {Button, Checkbox, CircularProgress, IconButton, InputAdornment, Link} from "@mui/material";
import {brand} from "../../../theme/brand";
import {Visibility, VisibilityOff} from "@mui/icons-material";
import {AuthContainer} from "../Auth.styled";
import {usePageTitle} from '../../../hooks/usePageTitle';
import LogoWithLanguageSwitcher from '../../../components/common/LogoWithLanguageSwitcher';
import {PageTitleKeys} from '../../../config/routeConstants';
import {parseError} from "../../../errors/http.service";
import {useTranslation} from 'react-i18next';
import {validatePassword} from "../../../utils/password-validation";
import {getEmailValidationError} from "../../../utils/email-validation";
import {normalizePhoneNumber, phoneInputProps, validatePhoneNumber} from "../../../utils/phone-validation";
import {validateRequiredTrimmed} from "../../../utils/required-validation";
import {ValidationError} from "../../../utils/validation";
import {authService, RegisterUserParamsType} from "../../../services/auth.service";
import {EulaAgreementResponse, termsService} from "../../../services/terms.service";
import EulaDialog from "../EulaDialog";
import axios from "axios";

async function registerUser({firstName, lastName, position, phoneNumber, email, password, eulaAccepted, eulaVersion}: RegisterUserParamsType) {
    try {
        await authService.registerUser({firstName, lastName, position, phoneNumber, email, password, eulaAccepted, eulaVersion});
        return true;
    } catch (error) {
        console.error("Registration error:", error);
        return false;
    }
}

const Register = () => {
    usePageTitle(PageTitleKeys.Register);

    const {t} = useTranslation();
    const navigate = useNavigate();
    const {showSnackbar} = useSnackbar();

    const [name, setName] = useState<string>("");
    const [nameTouched, setNameTouched] = useState<boolean>(false);

    const [surname, setSurname] = useState<string>("");
    const [surnameTouched, setSurnameTouched] = useState<boolean>(false);

    const [position, setPosition] = useState<string>("");

    const [phoneNumber, setPhoneNumber] = useState<string>("");
    const [phoneNumberTouched, setPhoneNumberTouched] = useState<boolean>(false);

    const [email, setEmail] = useState<string>("");
    const [emailTouched, setEmailTouched] = useState<boolean>(false);

    const [password, setPassword] = useState<string>("");
    const [passwordTouched, setPasswordTouched] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);

    const [submitted, setSubmitted] = useState<boolean>(false);
    const [consent, setConsent] = useState<boolean>(false);

    const [isFetchingToken, setIsFetchingToken] = useState<boolean>(false);


    const getErrorText = (showError: boolean, error: ValidationError | null) =>
        showError && error ? String(t(error.key)) : "";

    const firstNameValidationError = validateRequiredTrimmed(name, "inputDialog.required");
    const lastNameValidationError = validateRequiredTrimmed(surname, "inputDialog.required");
    const phoneNumberValidationError = validatePhoneNumber(phoneNumber);
    const emailValidationError = validateRequiredTrimmed(email, "inputDialog.required") ?? getEmailValidationError(email, true);
    const passwordValidationError = validateRequiredTrimmed(password, "inputDialog.required") ?? validatePassword(password);

    const showFirstNameError = (nameTouched || submitted) && !!firstNameValidationError;
    const showLastNameError = (surnameTouched || submitted) && !!lastNameValidationError;
    const showPhoneNumberError = (phoneNumberTouched || submitted) && !!phoneNumberValidationError;
    const showEmailError = (emailTouched || submitted) && !!emailValidationError;
    const showPasswordError = (passwordTouched || submitted) && !!passwordValidationError;

    const [eulaData, setEulaData] = useState<EulaAgreementResponse | null>(null);
    const [isEulaLoading, setIsEulaLoading] = useState<boolean>(true);
    const [eulaAccepted, setEulaAccepted] = useState<boolean>(false);
    const [isEulaDialogOpen, setIsEulaDialogOpen] = useState<boolean>(false);

    const fetchEulaContent = async () => {
        try {
            setIsEulaLoading(true);
            const data = await termsService.fetchEulaAgreement();
            setEulaData(data);
        } catch (e) {
            if (axios.isAxiosError(e) && !e.response) {
                return;
            }

            console.error(e);
            showSnackbar(t('register.fetchEulaError'), "error");
        } finally {
            setIsEulaLoading(false);
        }
    };

    useEffect(() => {
        void fetchEulaContent();
    }, []);

    const handleRegister = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setSubmitted(true);

        if (!consent
            || (!!eulaData && !eulaAccepted)
            || firstNameValidationError
            || lastNameValidationError
            || phoneNumberValidationError
            || passwordValidationError
            || emailValidationError
        ) {
            setSubmitted(false);
            return;
        }

        setIsFetchingToken(true);

        try {
            const passed = await registerUser({
                firstName: name,
                lastName: surname,
                position,
                phoneNumber,
                email,
                password,
                eulaAccepted,
                eulaVersion: eulaData?.version ?? '',
            });

            if (passed) {
                showSnackbar(t('register.successMessage'), 'success');
                navigate("/thank-you-for-registration");
            } else {
                showSnackbar(t('register.errorMessage'), 'error');
            }
        } catch (error) {
            console.error('Error during registration:', error);
            showSnackbar(parseError(error), "error");
        } finally {
            setIsFetchingToken(false);
            setSubmitted(false);
        }
    };

    return (
        <AuthContainer role="region" aria-labelledby="register-title">
            <LogoWithLanguageSwitcher src={brand.logo} alt={t('auth.logo.alt')} clickable={false} />

            <StyledCard>
                <Header id="register-title">{t('register.title')}</Header>
                <Subtext>{t('register.welcome')}</Subtext>

                <StyledForm onSubmit={handleRegister} autoComplete="off" aria-live="polite" aria-atomic={true} aria-busy={isFetchingToken}>
                    <Row>
                        <div style={{width: '100%'}}>
                            <StyledTextField
                                label={t('register.firstName')}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                onBlur={() => setNameTouched(true)}
                                required
                                error={showFirstNameError}
                                helperText={getErrorText(showFirstNameError, firstNameValidationError)}
                                fullWidth
                                disabled={isFetchingToken}
                                inputProps={{ 'aria-label': t('register.firstName.ariaLabel') as string }}
                            />
                            <div style={{height: '24px', marginTop: '4px'}}></div>
                        </div>

                        <div style={{width: '100%'}}>
                            <StyledTextField
                                label={t('register.lastName')}
                                value={surname}
                                onChange={(e) => setSurname(e.target.value)}
                                onBlur={() => setSurnameTouched(true)}
                                required
                                error={showLastNameError}
                                helperText={getErrorText(showLastNameError, lastNameValidationError)}
                                fullWidth
                                disabled={isFetchingToken}
                                inputProps={{ 'aria-label': t('register.lastName.ariaLabel') as string }}
                            />
                            <div style={{height: '24px', marginTop: '4px'}}></div>
                        </div>
                    </Row>

                    <Row>
                        <div style={{width: '100%'}}>
                            <StyledTextField
                                label={t('register.position')}
                                value={position}
                                onChange={(e) => setPosition(e.target.value)}
                                fullWidth
                                disabled={isFetchingToken}
                                inputProps={{ 'aria-label': t('register.position.ariaLabel') as string }}
                            />
                            <div style={{height: '24px', marginTop: '4px'}}></div>
                        </div>

                        <div style={{width: '100%'}}>
                            <StyledTextField
                                label={t('register.phoneNumber')}
                                type="tel"
                                onBlur={() => setPhoneNumberTouched(true)}
                                error={showPhoneNumberError}
                                helperText={getErrorText(showPhoneNumberError, phoneNumberValidationError)}
                                inputProps={{
                                    ...phoneInputProps,
                                    'aria-label': t('register.phoneNumber.ariaLabel') as string
                                }}
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(normalizePhoneNumber(e.target.value))}
                                fullWidth
                                variant="outlined"
                                disabled={isFetchingToken}
                            />
                            <div style={{height: '24px', marginTop: '4px'}}></div>
                        </div>
                    </Row>

                    <div>
                        <StyledTextField
                            label={t('register.email')}
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                            }}
                            onBlur={() => setEmailTouched(true)}
                            required
                            error={showEmailError}
                            helperText={getErrorText(showEmailError, emailValidationError)}
                            fullWidth
                            disabled={isFetchingToken}
                            inputProps={{ 'aria-label': t('register.email.ariaLabel') as string }}
                        />
                        <div style={{height: '24px', marginTop: '4px'}}></div>
                    </div>

                    <div>
                        <StyledTextField
                            label={t('register.password')}
                            type={showPassword ? "text" : "password"}
                            autoComplete="new-password"
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value);
                            }}
                            onBlur={() => setPasswordTouched(true)}
                            required
                            error={showPasswordError}
                            helperText={getErrorText(showPasswordError, passwordValidationError)}
                            fullWidth
                            disabled={isFetchingToken}
                            inputProps={{ 'aria-label': t('register.password.ariaLabel') as string }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            edge="end"
                                            sx={{color: 'primary.main'}}
                                            disabled={isFetchingToken}
                                            aria-label={t('register.password.toggleVisibility') as string}
                                        >
                                            {showPassword ? <VisibilityOff/> : <Visibility/>}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <div style={{height: '24px', marginTop: '4px'}}></div>
                    </div>

                    <RequiredFieldsNote>{t('register.requiredFieldsInfo')}</RequiredFieldsNote>

                    <CheckboxWrapper as="div">
                        <Checkbox
                            checked={consent}
                            onChange={(e) => setConsent(e.target.checked)}
                            sx={{padding: "0 8px 0 0"}}
                            disabled={isFetchingToken || isEulaLoading}
                            inputProps={{ 'aria-label': t('register.consent.ariaLabel') as string }}
                        />
                        <CheckboxText>
                            {t('register.consent')}
                        </CheckboxText>
                    </CheckboxWrapper>

                    {eulaData && (
                        <CheckboxWrapper as="div">
                            <Checkbox
                                required
                                checked={eulaAccepted}
                                onChange={(e) => setEulaAccepted(e.target.checked)}
                                sx={{padding: "0 8px 0 0"}}
                                disabled={isFetchingToken || isEulaLoading}
                                inputProps={{'aria-label': t('register.eulaAcceptance.ariaLabel') as string}}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setEulaAccepted(prev => !prev);
                                    }
                                }}
                            />
                            <CheckboxText>
                                {t('register.eulaAcceptance')}
                                <Link
                                    component="span"
                                    role="button"
                                    tabIndex={0}
                                    sx={{ cursor: "pointer" }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsEulaDialogOpen(true);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setIsEulaDialogOpen(true);
                                        }
                                    }}
                                >
                                    {t('register.eulaAcceptanceLink')}
                                </Link>
                                {' *'}
                            </CheckboxText>
                        </CheckboxWrapper>
                    )}

                    {(isFetchingToken || isEulaLoading) && (
                        <div role="status" aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }}>
                            {t('register.loading')}
                        </div>
                    )}

                    <Button type="submit"
                            variant="contained"
                            color="primary"
                            sx={{marginTop: "20px"}}
                            disabled={
                                !consent
                                || isFetchingToken
                                || isEulaLoading
                                || (!!eulaData && isEulaDialogOpen)
                                || (!!eulaData && !eulaAccepted)
                                || !!firstNameValidationError
                                || !!lastNameValidationError
                                || !!phoneNumberValidationError
                                || !!emailValidationError
                                || !!passwordValidationError
                            }
                    >
                        {(isFetchingToken || isEulaLoading)
                            ? <CircularProgress size={24} sx={{color: 'inherit'}}/>
                            : t('register.registerButton')
                        }
                    </Button>

                    <StyledLinkWrapper>
                        <span>{t('register.loginText')}</span>
                        <StyledLink
                            onClick={!isFetchingToken ? () => navigate("/login") : undefined}
                            disabled={isFetchingToken || isEulaLoading}
                        >
                            <strong>{t('register.loginLink')}</strong>
                        </StyledLink>
                    </StyledLinkWrapper>
                </StyledForm>
            </StyledCard>

            {(eulaData && isEulaDialogOpen) && (
                <EulaDialog open={isEulaDialogOpen} onClose={() => setIsEulaDialogOpen(false)} eulaData={eulaData} />
            )}
        </AuthContainer>
    );
};

export default Register;
